import {
  NoticeFormDefaultValues,
  NoticeFormProps,
  NoticeFormRawValues,
  NoticeFormType,
  NoticeFormValues
} from "./NoticeForm";
import {useTranslation} from "react-i18next";
import {createFormContext} from "@mantine/form";
import {
  adjustFromTimeZoneSafely,
  emptyOrUndefined,
  getDatabaseCodeSafely,
  getObjectId,
  noticeCategory,
  NoticeEntity,
  noticeKind,
  noticeSeverity,
  personCapabilityType,
  addDays,
} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";

export const toFormDefaultsFromNotice = (notice: NoticeEntity | undefined): Partial<NoticeFormValues> => {
  return {
    kind: notice?.kind,
    category: notice?.category,
    severity: notice?.severity,
    title: notice?.title,
    bodyText: notice?.bodyText,
    expiresDate: adjustFromTimeZoneSafely(notice?.expiresDttm),
  };
};

const getInitialValues = (defaultValues: NoticeFormDefaultValues | undefined): NoticeFormRawValues => {
  return {
    kind: defaultValues?.kind?.code,
    category: defaultValues?.category?.code,
    severity: defaultValues?.severity?.code,
    title: defaultValues?.title,
    bodyText: defaultValues?.bodyText,
    expiresDate: defaultValues?.expiresDate,
    regions: defaultValues?.regions?.map(getObjectId),
    companies: defaultValues?.companies?.map(getObjectId),
    audienceCapabilities: defaultValues?.audienceCapabilities?.map(getDatabaseCodeSafely),
  }
}

export const [NoticeFormProvider, useNoticeFormContext, useSharedNoticeForm] = createFormContext<NoticeFormRawValues>();

export type UseNoticeFormProps = Pick<NoticeFormProps, 'defaultValues'> & {}

export function useNoticeForm(props: UseNoticeFormProps): NoticeFormType {
  const {
    defaultValues
  } = props

  const {regionId} = useApp()
  const {companyId} = useAuth()
  const {t} = useTranslation(["common"])

  const form = useSharedNoticeForm({
    initialValues: getInitialValues({
      ...defaultValues,
      kind: defaultValues?.kind ?? noticeKind.notice,
      category: defaultValues?.category ?? noticeCategory.general,
      severity: defaultValues?.severity ?? noticeSeverity.major,
      expiresDate: defaultValues?.expiresDate ?? addDays(new Date(), 3),
      regions: emptyOrUndefined(defaultValues?.regions) ? (regionId ? [regionId] : []) : defaultValues?.regions,
      companies: emptyOrUndefined(defaultValues?.companies) ? (companyId ? [companyId] : []) : defaultValues?.companies,
      audienceCapabilities: emptyOrUndefined(defaultValues?.audienceCapabilities) ? [personCapabilityType.driver] : defaultValues?.audienceCapabilities,
    }),
    validate: {
      kind: (value) => value ? undefined : t("common.validation.required", "Required"),
      category: (value) => value ? undefined : t("common.validation.required", "Required"),
      severity: (value) => value ? undefined : t("common.validation.required", "Required"),
      title: (value) => value ? undefined : t("common.validation.required", "Required"),
      bodyText: (value) => value ? undefined : t("common.validation.required", "Required"),
    },
  })

  return form
}
